// ./src/components/features/Events/UpgradeOfferBanner.tsx
//
// ROADMAP #60 (MONETIZATION §7.6; owner, 2026-10-27) — "pay while you're here": the in-session upgrade offer while it is
// open, with its countdown and the way to the plans. Shown on the wizard's finish screen and the event dashboard (the
// plans drawer shows the prices). The SERVER decides everything — whether there is an offer (partylabz, an event made
// from scratch, its first hour), the percent and the time left (`EventService.upgradeOffer`, read here on its own so an
// event read never depends on it); this only counts down.
// ⚠ Nothing at all without an offer, and nothing once it runs out — a closed offer is never shown (it would be a promise
//   the checkout no longer keeps). Host-facing only: never on a guest page.
import React, { useEffect, useState } from "react";
import { Alert, Button, Group, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";
import { formatOfferCountdown, useOfferCountdown } from "@/hooks/useOfferCountdown";
import { EventService, EventUpgradeOfferInfo } from "@/utils/api";

export interface UpgradeOfferBannerProps {
  eventId: string | null | undefined;
  /** Changes when the page re-reads the event, so the offer is read again with it. */
  reloadKey?: unknown;
  /** Opens the plans (the dashboard's `/events/:id/plans` stage). */
  onSeePlans: () => void;
}

export const UpgradeOfferBanner: React.FC<UpgradeOfferBannerProps> = ({ eventId, reloadKey, onSeePlans }) => {
  const { t } = useTranslation(["events"]);
  const [offer, setOffer] = useState<EventUpgradeOfferInfo | null>(null);
  const left = useOfferCountdown(offer?.secondsLeft);

  useEffect(() => {
    let current = true;

    setOffer(null);

    if (!eventId) {
      return;
    }

    void EventService.upgradeOffer(eventId).then((read) => {
      if (current) {
        setOffer(read);
      }
    });

    return () => {
      current = false;
    };
  }, [eventId, reloadKey]);

  if (!offer || offer.percentOff <= 0 || left <= 0) {
    return null;
  }

  return (
    <Alert color="brand" variant="light" radius="md" icon={<AppIcon iconName="Gift" size={18} />}>
      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Text size="md">
          {t("events:upgradeOffer.banner", {
            defaultValue: "{{percent}}% off this event's upgrade for the next {{time}}.",
            percent: offer.percentOff,
            time: formatOfferCountdown(left),
          })}
        </Text>
        <Button size="md" variant="filled" leftSection={<AppIcon iconName="Rocket" size={16} />} onClick={onSeePlans}>
          {t("events:upgradeOffer.seePlans", { defaultValue: "See upgrades" })}
        </Button>
      </Group>
    </Alert>
  );
};
